import {
	Button,
	Flex,
	Rte,
	Text,
	colors,
	Image,
	scale,
	breakpoints,
} from "@capioweb/ui-components/src"
import { assetBaseUrl, createMarkup } from "../../utils"
import styled from "styled-components"

import React from "react"
import { NewsFeedItemProps } from "../../pages"
import { BlockCommonLayout } from "../../utils/layout"
import { StringProperty } from "../../@types/EpiFoundationSpa/Property"
import ContentLink from "../../@types/EpiFoundationSpa/Models/ContentLink"

interface NewsFeedPageBlockProps {
	items: NewsFeedItemProps[]
	blockHeading: StringProperty
	blockLinkText: StringProperty
	contentLink: ContentLink
}

interface StyledTextFadeProps {
	hasImage: boolean
}

const StyledTextFade = styled.div<StyledTextFadeProps>`
	display: ${({ hasImage }) => (hasImage ? "none" : "flex")};
	flex-direction: column;
	width: 100%;
	height: ${scale.px(8)};
	background: linear-gradient(rgba(255, 255, 255, 0), ${colors.white} 75%);
	z-index: 1;

	@media screen and (min-width: ${breakpoints.m}px) {
		display: flex;
	}
`

const NewsFeedPageBlock: React.FC<NewsFeedPageBlockProps> = ({
	items,
	blockHeading,
	blockLinkText,
	contentLink,
}) => {
	// Get the latest news item
	const latestNewsItem = items[0]
	// Check if the latest news item has an image
	const hasImage = !!latestNewsItem?.image?.media?.value

	return (
		<BlockCommonLayout heading={blockHeading}>
			{/* Main Flex container */}
			<Flex
				w={1}
				flexDirection={{ _: "column", m: "row" }}
				columnGap={3}
				rowGap={3}
				justifyContent="center"
			>
				{/* Display image if it exists */}
				{hasImage && (
					<Flex w={{ _: 1, m: "50%" }}>
						<Image
							src={assetBaseUrl() + latestNewsItem?.image?.media?.value?.url}
							alt={latestNewsItem?.image?.alternateText?.value}
							title={latestNewsItem?.image?.imageTitle?.value}
							width={552}
							style={{ width: "100%", objectFit: "cover", aspectRatio: "16/9" }}
						/>
					</Flex>
				)}
				{/* Flex container for text content */}
				<Flex
					w={{ _: 1, m: hasImage ? "50%" : 1 }}
					flexDirection="column"
					position="relative"
					h={!hasImage && 38}
					maxw={{ m: 69 }}
				>
					{/* Flex container for label, heading, and body text */}
					<Flex
						h={1}
						overflow={{ _: hasImage ? "visible" : "hidden", m: "hidden" }}
						position={{
							_: hasImage ? "static" : "absolute",
							m: "absolute",
						}}
						flexDirection="column"
						rowGap={1.5}
						mb={3}
					>
						{/* Label / date */}
						<Text as="time" variant="paragraph-16" mb={0}>
							{latestNewsItem?.date}
						</Text>
						{/* Heading */}
						{latestNewsItem.heading &&
							<Text variant="headline-3" mb={0}>
								{latestNewsItem.heading}
							</Text>
						}
						{latestNewsItem?.preamble &&
							<Text variant="paragraph-16" mb={0}>
								{latestNewsItem.preamble}
							</Text>
						}
						{/* Body text */}
						<Flex w={1} display={{ _: hasImage ? "none" : "flex", m: "flex" }}>
							<Rte>
								<div
									dangerouslySetInnerHTML={createMarkup(latestNewsItem?.body)}
								></div>
							</Rte>
						</Flex>
					</Flex>
					{/* Flex container for text fade and link button */}
					<Flex h={1} justifyContent="flex-end" flexDirection="column">
						{/* Text fade  */}
						<StyledTextFade hasImage={hasImage} />
						{/* Link button  */}
						<Flex w={1} backgroundColor="white" zIndex={1}>
							<Flex w={{ _: 1, m: "auto" }} zIndex={1} bottom={{ m: 0 }}>
								<Button.Link
									variant="ghost"
									iconPosition="right"
									icon="arrow-right"
									textColor="navy"
									to={assetBaseUrl() + contentLink?.url}
									style={{ width: "100%" }}
								>
									<Text mb={0} ml={1} variant="paragraph-14">
										{blockLinkText?.value}
									</Text>
								</Button.Link>
							</Flex>
						</Flex>
					</Flex>
				</Flex>
			</Flex>
		</BlockCommonLayout>
	)
}

export { NewsFeedPageBlock }
